import { useContext, useMemo } from "react"

import { IGatsbyImageData } from "gatsby-plugin-image"
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import styled from "styled-components"

import Carousel from "components/Carousels/Carousel"
import carouselData from "components/Carousels/CarouselData"
import MobileCarousel from "components/MobileCarousel"
import { ScreenContext } from "components/Providers"
import colors from "styles/colors"
import media from "styles/media"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"

interface Props {
  allCarouselImages: Record<string, IGatsbyImageData | undefined>
}

export default function HomeCarousel({ allCarouselImages }: Props) {
  const { solutions } = carouselData
  const { mobile } = useContext(ScreenContext)
  const { t } = useTranslation()

  const {
    coreVal,
    coreMVal,
    digitalVal,
    digitalMVal,
    cardVal,
    cardsMVal,
    riskVal,
    riskMVal,
    paymentsVal,
    paymentsValM,
    securityVal,
    securityMVal,
  } = allCarouselImages

  const core = useMedia(coreVal, coreVal, coreVal, coreMVal)
  const digital = useMedia(digitalVal, digitalVal, digitalVal, digitalMVal)
  const cards = useMedia(cardVal, cardVal, cardVal, cardsMVal)
  const payments = useMedia(paymentsVal, paymentsVal, paymentsVal, paymentsValM)
  const security = useMedia(securityVal, securityVal, securityVal, securityMVal)
  const risk = useMedia(riskVal, riskVal, riskVal, riskMVal)
  const { language } = useI18next()

  const buttonsArr = useMemo(
    () => [
      { buttonText: t("Cyberbank Core") },
      { buttonText: t("Cyberbank Digital") },
      { buttonText: t("Card Issuing") },
      { buttonText: t("Payment Hub") },
      { buttonText: t("Secured Credit") },
      { buttonText: t("Payment Risk Platform") },
    ],
    [t]
  )

  const sections = useMemo(
    () => [
      {
        body: t(
          "A next-generation, cloud-native platform designed to accelerate innovation, enhance customer experiences and boost operational efficiency."
        ),
        link: {
          text: t("Explore Cyberbank Core"),
          url: checkedLocaleLink("/deposits/cyberbank-core/", language),
        },
        img: core,
        alt: t("Cyberbank Core"),
      },
      {
        body: t(
          "A cloud-native, composable, microservices-driven platform designed to help companies build innovative, user-centric financial experiences."
        ),
        link: {
          text: t("Explore Cyberbank Digital"),
          url: checkedLocaleLink("/channels/cyberbank-digital/", language),
        },
        img: digital,
        alt: t("Cyberbank Digital"),
      },
      {
        body: t(
          "Card issuing includes issuing physical, virtual and digital-first cards, which are essential to cardholder activation and retention."
        ),
        link: {
          text: t("Explore Cards"),
          url: checkedLocaleLink("/payments-and-cards/card-issuing/", language),
        },
        img: cards,
        alt: t("Cards"),
      },
      {
        body: t(
          "Seamless and secure transactional experiences are the baseline for payment processing and money transfers today."
        ),
        link: {
          text: t("Explore Payment Hub"),
          url: checkedLocaleLink("/payments-and-cards/payment-hub/", language)
         
        },
        img: payments,
        alt: t("Payment details UI"),
      },
      {
        body: t(
          "Helping consumers and businesses achieve their financial goals requires adaptable credit and lending programs that fit their unique, evolving needs."
        ),
        link: {
          text: t("Explore Secured Credit"),
          url: checkedLocaleLink("/lending/secured-credit/", language),
        },
        img: security,
        alt: t("Secured"),
      },
      {
        body: t(
          "Payment fraud is complicated, and card detection can be tricky. But we're here to help you with proactive risk and compliance management."
        ),
        link: {
          text: t("Explore Payment Risk Platform"),
          url: checkedLocaleLink("/risk/payment-risk-platform/", language),
        },
        img: risk,
        alt: t("Risk"),
      },
    ],
    [t, language, cards, core, digital, payments, security, risk]
  )

  return mobile ? (
    <MobileCarousel
      gradient={{
        gradient: `${colors.turqBlueHorz}`,
        fill: `${colors.blue400}`,
      }}
      sections={sections}
      buttonsArr={buttonsArr}
      calloutText={solutions}
    />
  ) : (
    <StyledCarousel
      gradient={{
        gradient: `${colors.turqBlueHorz}`,
        fill: `${colors.blue400}`,
      }}
      className="homepage_carousel"
      sections={sections}
      buttonsArr={buttonsArr}
      calloutText={solutions}
    />
  )
}

const StyledCarousel = styled(Carousel)`
  ${media.fullWidth} {
    padding: 144px 0 180px;
  }
  ${media.desktop} {
    padding: 8.675vw 0 10.843vw;
  }
`
